import { useCallback } from "react";
import { cosmosApiUrl, isLocalCosmosApi, localCosmosHeaders } from "@/lib/cosmos-api-base-url";
import { useAihrAuth } from "@/lib/auth/useAihrAuth";

export const COSMOS_ID_TOKEN_HEADER = "x-cosmos-id-token";

export async function attachCosmosAuthHeaders(
  headers: Headers,
  options: {
    authEnabled: boolean;
    getAccessToken: () => Promise<string | undefined>;
    getIdToken: () => Promise<string | undefined>;
  }
): Promise<void> {
  const token = options.authEnabled ? await options.getAccessToken() : undefined;
  if (token) {
    headers.set("authorization", `Bearer ${token}`);
    const idToken = await options.getIdToken();
    if (idToken) {
      headers.set(COSMOS_ID_TOKEN_HEADER, idToken);
    }
    return;
  }
  if (!isLocalCosmosApi()) {
    throw new Error("Auth0 is not configured for this Cosmos app environment.");
  }
  for (const [key, value] of Object.entries(localCosmosHeaders())) {
    headers.set(key, String(value));
  }
}

export function useCosmosApi() {
  const { authEnabled, getAccessToken, getIdToken } = useAihrAuth();

  return useCallback(
    async <T,>(
      path: string,
      options: RequestInit & { body?: BodyInit | null } = {}
    ): Promise<T> => {
      const headers = new Headers(options.headers);
      headers.set("accept", "application/json");
      if (options.body && !headers.has("content-type")) {
        headers.set("content-type", "application/json");
      }
      await attachCosmosAuthHeaders(headers, {
        authEnabled,
        getAccessToken,
        getIdToken
      });

      const response = await fetch(cosmosApiUrl(path), {
        ...options,
        headers
      });
      const text = await response.text();
      const data = text ? (JSON.parse(text) as T) : (undefined as T);
      if (!response.ok) {
        throw new Error(
          typeof data === "object" && data && "message" in data
            ? String((data as { message: unknown }).message)
            : `Cosmos request failed with ${response.status}`
        );
      }
      return data;
    },
    [authEnabled, getAccessToken, getIdToken]
  );
}
